import React, {useContext, useEffect, useState} from "react";
import {Title} from "../../../../../../stories/Title/Title";
import {Button} from "../../../../../../stories/Button/Button";
import {useNavigate} from "react-router-dom";
import Input from "../../../../../../stories/Input/Input";
import Navbar from "../../../../../common/layout/Navbar";
import {LicenseTypes} from "../../../../../../utils/Enum.types";
import EyeWhite from "../../../../../../images/EyeWhite.svg";
import useUrlParams from "../../../../../../customHooks/useUrlParams";
import {AppContext} from "../../../../../../context/context";
import AssetList from "./AssetList";
import {useLazyQuery} from "@apollo/client";
import {
  AtsItemGroupEdge,
  AtsItemGroupOrderField,
  GetItemGroupsResult,
  GetItemGroupsVars,
} from "../queries/types";
import {GetItemGroupsForAssign} from "../queries/queries";
import {
  CARDS_ROWS_PER_PAGE,
  FETCH_MORE_DATA,
  debouncedDelay,
} from "../../../../../../utils/constants";
import {OrderDirection} from "../../../../../../utils/commonQueryTypes";
import {getModifiedScrollHeight} from "../../../../../../utils/UtilFunctions";
import { useDebounce } from "../../../../../../customHooks/useDebounce";

const AssignAsset = () => {
  const navigate = useNavigate();
  const {customer_id} = useUrlParams();

  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);

  const [GetGroups, {loading, data, fetchMore}] = useLazyQuery<
    GetItemGroupsResult,
    GetItemGroupsVars
  >(GetItemGroupsForAssign);

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [itemGroups, setItemGroups] = useState<AtsItemGroupEdge[]>([]);

  useEffect(() => {
    if (customer_id) {
      GetGroups({
        variables: {
          customerID: customer_id,
          after: null,
          first: CARDS_ROWS_PER_PAGE,
          name: debouncedSearch,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsItemGroupOrderField.GROUP_NAME,
          },
        },
      });
    }
  }, [customer_id, GetGroups, debouncedSearch]);

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, {fetchMoreResult}) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetItemGroups.edges;
            const pageInfo = fetchMoreResult.GetItemGroups.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetItemGroups.edges.filter(
              ({node: {id}}) =>
                newEdges.findIndex(({node: {id: newId}}) => newId === id) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetItemGroups: {
                edges: [...itemGroups, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetItemGroups.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItemGroups.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = itemGroups.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItemGroups(updatedNewData);
      } else {
        setItemGroups(newData);
      }

      setEndCursor(data.GetItemGroups.pageInfo.endCursor);
      setHasNextPage(data.GetItemGroups.pageInfo.hasNextPage);
    } // eslint-disable-next-line
  }, [data, loading]);

  return (
    <>
      <Navbar NavType={LicenseTypes.COMPANY} />
      <Title>Grouping Asset</Title>
      <div className="assign-asset">
        <div className="assign-asset__search row g-0">
          <div className="col-2">
            <Input
              id="data_search"
              placeholder="Search..."
              onChange={(e) => {
                setSearchData(e.target.value);
              }}
            />
          </div>
          <div className="col"></div>
          <div className="col-2 flex-end">
            <div className="overall-total-count">
              Total Group: <b>{data ? data.GetItemGroups.totalCount : 0}</b>
            </div>
          </div>
        </div>
        <AssetList itemGroups={itemGroups} handleScroll={handleScroll} />
        <Button mode="back" onClick={() => navigate(-1)} />
      </div>
    </>
  );
};

export default AssignAsset;
